import axios from 'axios';

const isBrowser = typeof window !== 'undefined';

const isLocalHostName = (hostname) => ['localhost', '127.0.0.1', '::1'].includes(hostname);

const isCrossOriginApiBase = (apiBase, origin) => {
  if (!apiBase || !origin) return false;
  try {
    return new URL(apiBase, origin).origin !== origin;
  } catch {
    return false;
  }
};

// API base URL resolution:
//
//  1. window.__API_BASE__  — runtime config loaded from public/config.js or
//                            generated by entrypoint.sh. This is normally /api.
//  2. /api                 — production browser fallback for split deployments.
//                            Email/password auth must use the same-origin proxy
//                            so auth and CSRF cookies are first-party cookies.
//  3. REACT_APP_API_URL    — local development / explicit direct-API fallback.
//
// Direct browser calls from a Vercel/static frontend to a Railway API make the
// API cookies third-party cookies. Firefox/Safari and privacy extensions may
// block those cookies, which breaks CSRF and the post-registration session.
// Use REACT_APP_USE_DIRECT_API=true only for deployments that intentionally do
// not provide a same-origin /api proxy and can tolerate third-party cookies.
export const resolveApiBaseURL = ({
  runtimeApiBase,
  envApiBase,
  useDirectApi,
  location,
} = {}) => {
  if (runtimeApiBase) return runtimeApiBase;

  const shouldPreferSameOriginProxy =
    location &&
    envApiBase &&
    isCrossOriginApiBase(envApiBase, location.origin) &&
    !isLocalHostName(location.hostname) &&
    useDirectApi !== 'true';

  return (shouldPreferSameOriginProxy ? '/api' : envApiBase) || '/api';
};

const baseURL = resolveApiBaseURL({
  runtimeApiBase: isBrowser ? window.__API_BASE__ : null,
  envApiBase: process.env.REACT_APP_API_URL,
  useDirectApi: process.env.REACT_APP_USE_DIRECT_API,
  location: isBrowser ? window.location : null,
});

// Request deadlines. Before these existed the client had NO timeout at all, so any
// request could hang until the customer closed the tab.
//
// A single duration cap cannot serve both cases here. Ordinary JSON calls should
// fail fast; an upload must not be cut off for being slow. Twenty photos at 20 MB
// on a rural or mobile connection can legitimately run for a very long time, and
// killing that upload halfway is a far worse outcome than a slow one — so uploads
// get no duration cap at all and are policed by a stall watchdog instead (see
// attachUploadStallWatchdog).
export const DEFAULT_TIMEOUT_MS = 30_000;

// Endpoints that legitimately take longer than the default. Kept here rather than at
// the call sites: the pages that hit these were working fine with no timeout at all,
// and they should not have to learn about transport deadlines just because one was
// introduced. /verify reconciles with Stripe; /render queues the job (its progress is
// polled separately, under the 32-minute backstop in NewProjectPage).
export const LONG_REQUEST_TIMEOUT_MS = 120_000;
const LONG_REQUEST_PATHS = [/\/payments\/[^/]+\/verify$/, /\/videos\/[^/]+\/render$/];

// How long an upload may transfer ZERO bytes before we give up on it. This is not a
// total duration: every progress event resets it, so an upload that is moving at any
// speed is never interrupted. Only a connection that has genuinely died is aborted.
// A caller can tighten or relax it per request with `uploadStallTimeout`.
export const UPLOAD_STALL_TIMEOUT_MS = 120_000;

// Once the last byte is sent, progress events stop and the server starts working —
// so from that moment the stall window is measuring the WRONG thing and has to be
// replaced by a much longer response deadline.
//
// The photo endpoint earns every second of this. It AV-scans sequentially with an 8s
// timeout per file (server/middleware/upload.js) for a batch of up to 50, then loops
// again to rotate with sharp and push each file to storage. Worst case is several
// minutes of complete silence on a perfectly healthy request. Aborting there would be
// worse than the hang this whole mechanism exists to prevent: NewProjectPage retries
// a no-response error, so the batch would be re-sent while the server was still
// busy storing the first copy — duplicating a family's photos.
export const UPLOAD_RESPONSE_TIMEOUT_MS = 900_000;   // 15 min

const api = axios.create({
  baseURL,
  timeout: DEFAULT_TIMEOUT_MS,
  headers: { 'Content-Type': 'application/json' },
  withCredentials: true,  // send HttpOnly cookies on every request
  // Disable axios's built-in XSRF cookie→header injection. By default axios copies
  // the XSRF-TOKEN cookie into the X-XSRF-Token header *after* our request
  // interceptor runs, which would overwrite the fresh server-issued token we
  // choose with a returning visitor's STALE/invalid cookie value — the real cause
  // of the intermittent first-login "couldn't verify your session security token"
  // 403. We set X-XSRF-Token ourselves (see the request interceptor) from a
  // server-validated token, so axios must not second-guess it.
  withXSRFToken: false,
});

// Read XSRF-TOKEN from cookie when the API is same-site. For cross-origin
// deployments (for example Vercel app + Railway API), the browser cannot read
// the API domain cookie from document.cookie, so we also cache the token exposed
// by the server's X-CSRF-Token response header.
let csrfTokenFromHeader = null;
let csrfBootstrapPromise = null;
// Whether we have obtained a SERVER-issued token this session. Set the moment any
// response carries an X-CSRF-Token (every response passes the server's CSRF
// middleware, which validates/re-issues the cookie and mirrors it in that header).
let csrfBootstrapped = false;

const rememberCsrfToken = (response) => {
  const csrf = response?.headers?.['x-csrf-token'] || response?.data?.csrfToken;
  if (csrf) {
    csrfTokenFromHeader = csrf;
    csrfBootstrapped = true;
  }
  return csrf || null;
};

const getCsrfToken = () => {
  // Prefer the token the server issued us this session (mirrored in the
  // X-CSRF-Token response header): it is guaranteed to be a currently-valid signed
  // token and to match the cookie the server just set. document.cookie is only a
  // fallback for the very first request before any response is seen — and is
  // deliberately NOT preferred, because a returning visitor can still hold a STALE
  // or invalid XSRF-TOKEN cookie from an earlier deploy. Sending that stale cookie
  // value as X-XSRF-Token is exactly what produced the intermittent first-login
  // "couldn't verify your session security token" 403.
  if (csrfTokenFromHeader) return csrfTokenFromHeader;
  if (!isBrowser) return null;
  const match = document.cookie.match(/(?:^|;\s*)XSRF-TOKEN=([^;]+)/);
  return match ? decodeURIComponent(match[1]) : null;
};

// Test seam: reset the in-memory CSRF state so each test starts from a clean
// "cold" session. No-op cost in production; never called there.
export const __resetCsrfStateForTests = () => {
  csrfTokenFromHeader = null;
  csrfBootstrapPromise = null;
  csrfBootstrapped = false;
};

const fetchCsrfToken = async ({ force = false } = {}) => {
  // When forcing a refresh (after a CSRF 403), drop the cached header token so a
  // stale value can't satisfy getCsrfToken() on the retry.
  if (force) csrfTokenFromHeader = null;
  if (csrfBootstrapPromise) return csrfBootstrapPromise;

  // Bare axios, not `api` — deliberately bypasses the interceptors to avoid
  // recursion. That also means it does not inherit the instance timeout, so pass
  // it explicitly. (Setting axios.defaults.timeout would not help: axios.create
  // snapshots the defaults at creation, so the instance and these bare calls have
  // to be configured separately.)
  csrfBootstrapPromise = axios.get(`${baseURL}/auth/csrf`, {
    headers: { Accept: 'application/json' },
    withCredentials: true,
    timeout: DEFAULT_TIMEOUT_MS,
  }).then((response) => rememberCsrfToken(response) || getCsrfToken())
    .finally(() => {
      csrfBootstrapPromise = null;
    });

  return csrfBootstrapPromise;
};

// Eagerly establish the double-submit CSRF cookie (and cached header token) at
// app / auth-page init, before the user can submit a state-changing request.
// Without this the very first login POST races the lazy bootstrap: the fresh
// XSRF-TOKEN cookie may not be committed yet, so the request falls through to the
// header-only path and can 403 until a second attempt. Safe to call repeatedly —
// it's a no-op once a token is already available.
export const ensureCsrfToken = async () => {
  // Bootstrap a SERVER-issued token once per session. We intentionally do NOT
  // short-circuit on a pre-existing document.cookie value: a returning visitor may
  // hold a stale/invalid XSRF-TOKEN cookie, and trusting it would send that invalid
  // token on the first POST — the root cause of the intermittent first-login 403.
  // One round-trip guarantees the server has validated/re-issued the cookie and
  // handed us a currently-valid token. Cheap thereafter (csrfBootstrapped short-
  // circuit), and de-duplicated via csrfBootstrapPromise while in flight.
  if (csrfBootstrapped) return getCsrfToken();
  try { return await fetchCsrfToken(); } catch { return getCsrfToken(); }
};

// Caller-supplied onUploadProgress/signal, kept so a retry can be re-instrumented
// from the original values rather than from the previous attempt's wrappers.
const uploadPristine = new WeakMap();

// Give an upload no duration cap, and police it by progress instead: abort only
// after the stall window elapses without a single byte moving. A slow upload keeps
// resetting the timer and runs as long as it needs; a dead socket — laptop lid
// closed, tunnel, dropped wifi — still fails instead of hanging forever.
//
// Mutates `config` and returns a cleanup function. Cleanup is returned rather than
// done here because the timer must outlive this call: it has to keep running for the
// whole request and be cleared once it settles (see the interceptors below).
const attachUploadStallWatchdog = (config) => {
  config.timeout = 0;  // no total cap — the watchdog is the only deadline

  // The CSRF and 401-refresh retries below re-send the SAME config object, so this
  // can run more than once per request. Stash the caller's original values the first
  // time and restore them on every re-entry; otherwise each retry would wrap the
  // progress callback in another layer and, worse, inherit the previous attempt's
  // already-aborted signal — which would kill the retry the instant it started.
  if (!uploadPristine.has(config)) {
    uploadPristine.set(config, {
      onUploadProgress: config.onUploadProgress,
      signal: config.signal,
    });
  }
  const pristine = uploadPristine.get(config);

  const controller = new AbortController();
  // Respect a caller-supplied signal by forwarding its abort to ours. No caller
  // passes one today; this keeps the watchdog from silently swallowing one later.
  if (pristine.signal) {
    if (pristine.signal.aborted) controller.abort();
    else pristine.signal.addEventListener('abort', () => controller.abort(), { once: true });
  }
  config.signal = controller.signal;

  const stallMs = config.uploadStallTimeout ?? UPLOAD_STALL_TIMEOUT_MS;
  const responseMs = config.uploadResponseTimeout ?? UPLOAD_RESPONSE_TIMEOUT_MS;

  let timer = null;
  let finished = false;
  let sent = false;   // all request bytes transferred; the server is working now
  const clear = () => {
    finished = true;
    if (timer) { clearTimeout(timer); timer = null; }
  };
  const arm = (ms) => {
    if (finished) return;
    if (timer) clearTimeout(timer);
    timer = setTimeout(() => { timer = null; controller.abort(); }, ms);
  };

  // Wrap rather than replace: NewProjectPage drives its progress bar from this.
  const callerOnUploadProgress = pristine.onUploadProgress;
  config.onUploadProgress = (event) => {
    // Once every byte is out the door there will be no further progress events, so
    // the stall window would be counting down against the server's own work. Hand
    // over to the response deadline instead, once, and stop re-arming.
    if (!sent) {
      if (event && event.total > 0 && event.loaded >= event.total) {
        sent = true;
        arm(responseMs);
      } else {
        arm(stallMs);
      }
    }
    if (callerOnUploadProgress) callerOnUploadProgress(event);
  };

  arm(stallMs);   // cover the gap before the first progress event
  return clear;
};

// Cleanup functions for in-flight uploads, keyed by the config object the
// interceptors both see. A WeakMap so a request that never settles cannot pin its
// config in memory.
const uploadWatchdogs = new WeakMap();

const clearUploadWatchdog = (config) => {
  if (!config) return;
  const clear = uploadWatchdogs.get(config);
  if (clear) {
    clear();
    uploadWatchdogs.delete(config);
  }
};

const STATE_CHANGING_METHODS = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);

// Auth bootstrap endpoints accept a header-only CSRF token before any session
// exists, so a fresh token is enough to recover them after a 403.
const CSRF_BOOTSTRAP_PATHS = ['/auth/register', '/auth/login', '/auth/guest', '/auth/demo'];

const shouldRetryCsrf = (error) => {
  const originalRequest = error.config;
  if (!originalRequest || originalRequest._csrfRetry) return false;
  if (error.response?.status !== 403) return false;
  const method = (originalRequest.method || 'get').toUpperCase();
  if (!STATE_CHANGING_METHODS.has(method)) return false;
  const url = originalRequest.url || '';
  return error.response?.data?.code === 'CSRF_TOKEN_INVALID' ||
    CSRF_BOOTSTRAP_PATHS.some(p => url.includes(p));
};

// Request interceptor — inject CSRF token on state-changing requests; fix FormData
api.interceptors.request.use(
  async (config) => {
    const method = (config.method || 'get').toUpperCase();
    if (!['GET', 'HEAD', 'OPTIONS'].includes(method)) {
      // Guarantee a SERVER-issued CSRF token before the first state-changing
      // request (bootstrap once per session), rather than trusting whatever is in
      // document.cookie — a returning visitor's stale cookie would otherwise be
      // sent verbatim and rejected. Best-effort: a transient /auth/csrf hiccup or a
      // blocked cookie must NOT abort the request (that would silently kill
      // CSRF-exempt POSTs like /auth/exchange-code and make sign-in "look logged
      // out"); if the endpoint really needs a token, the server 403s and the
      // response interceptor's self-healing retry recovers.
      await ensureCsrfToken();
      const csrf = getCsrfToken();
      if (csrf) config.headers['X-XSRF-Token'] = csrf;
    }
    // For FormData uploads, remove the default application/json Content-Type so the
    // browser can set multipart/form-data with the correct boundary automatically.
    if (config.data instanceof FormData) {
      if (typeof config.headers.delete === 'function') {
        config.headers.delete('Content-Type');
      } else {
        delete config.headers['Content-Type'];
      }
      // Uploads opt out of the instance timeout entirely — see the constants at the
      // top of this file for why a duration cap is the wrong tool here. Retire any
      // watchdog left over from a previous attempt at this same config before
      // arming a fresh one, so a retry can never leave an orphaned timer running.
      clearUploadWatchdog(config);
      uploadWatchdogs.set(config, attachUploadStallWatchdog(config));
    } else if (
      config.timeout === DEFAULT_TIMEOUT_MS &&
      LONG_REQUEST_PATHS.some(p => p.test(config.url || ''))
    ) {
      // Only lift the default — never override a timeout the caller chose itself.
      config.timeout = LONG_REQUEST_TIMEOUT_MS;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Silent refresh queue — when a 401 happens while a refresh is already in flight,
// queue the failed request so it retries once refresh completes.
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error) => {
  failedQueue.forEach(p => error ? p.reject(error) : p.resolve());
  failedQueue = [];
};

const clearAuthAndNotify = () => {
  window.dispatchEvent(new Event('auth:logout'));
};

// Response interceptor — on 401, attempt a silent refresh before logging out.
api.interceptors.response.use(
  (response) => {
    clearUploadWatchdog(response.config);
    rememberCsrfToken(response);
    return response;
  },
  (error) => {
    // Stop the stall timer on every failure path, including the abort the watchdog
    // itself fired. Retries below re-enter the request interceptor and get a fresh
    // watchdog, so releasing this one here cannot leave a retry unpoliced.
    clearUploadWatchdog(error.config);
    rememberCsrfToken(error.response);

    const originalRequest = error.config;
    const status = error.response?.status;

    // A share-access challenge (e.g. a gated photo/preview stream returning
    // { requiresVerification: true }) is NOT a session expiry. Never let it
    // trigger a token refresh or a global logout — otherwise one gated media
    // element would sign the whole user out. Just reject and let the caller decide.
    if (status === 401 && error.response?.data?.requiresVerification) {
      return Promise.reject(error);
    }

    // Self-healing CSRF retry — a state-changing request rejected with 403 for a
    // missing/stale double-submit token re-bootstraps a fresh token and retries
    // once. This recovers registration/login when the first token never loaded
    // (blocked third-party cookie, stale cache) without the user retrying.
    if (shouldRetryCsrf(error)) {
      originalRequest._csrfRetry = true;
      return fetchCsrfToken({ force: true })
        .then((csrf) => {
          if (csrf) originalRequest.headers['X-XSRF-Token'] = csrf;
          return api(originalRequest);
        });
    }

    // Don't try to refresh the refresh call itself, or retry the same request twice.
    if (status !== 401 || originalRequest?._retry || originalRequest?.url?.includes('/auth/refresh')) {
      if (status === 401 && !originalRequest?.url?.includes('/auth/refresh')) {
        clearAuthAndNotify();
      }
      return Promise.reject(error);
    }

    if (isRefreshing) {
      return new Promise((resolve, reject) => failedQueue.push({ resolve, reject }))
        .then(() => api(originalRequest));
    }

    originalRequest._retry = true;
    isRefreshing = true;

    return new Promise((resolve, reject) => {
      // POST to /auth/refresh — sf_refresh cookie is sent automatically via withCredentials.
      // Bare axios again, so the timeout has to be passed explicitly; without it a
      // stalled refresh would hang every request queued behind it.
      axios.post(`${baseURL}/auth/refresh`, {}, {
        withCredentials: true,
        timeout: DEFAULT_TIMEOUT_MS,
      })
        .then((response) => {
          rememberCsrfToken(response);
          processQueue(null);
          resolve(api(originalRequest));
        })
        .catch((refreshErr) => {
          rememberCsrfToken(refreshErr.response);
          processQueue(refreshErr);
          clearAuthAndNotify();
          reject(refreshErr);
        })
        .finally(() => {
          isRefreshing = false;
        });
    });
  }
);

// Friendly, human copy for status codes whose raw body carries no useful
// `error` string (e.g. a 403 from the CSRF/edge layer, or a network failure).
// Avoids surfacing axios's opaque "Request failed with status code N".
const STATUS_MESSAGES = {
  403: "We couldn't verify your session security token. Please refresh the page and try again.",
  429: 'Too many attempts. Please wait a moment and try again.',
  500: 'Something went wrong on our end. Please try again in a moment.',
  502: 'Something went wrong on our end. Please try again in a moment.',
  503: 'The server is starting up. Please retry in a moment.',
};

// Turns a Zod field-error key (e.g. "expiresAt" or "discountValue") into
// readable copy (e.g. "Expires At") so validation messages can name the
// specific field that's missing or invalid, not just "Validation failed".
const humanizeFieldName = (key) => {
  const last = key.split('.').pop();
  return last.replace(/([A-Z])/g, ' $1').replace(/^./, (c) => c.toUpperCase()).trim();
};

export const handleApiError = (error, fallback = 'An error occurred') => {
  const data = error.response?.data;

  if (data?.errors) {
    const errs = data.errors;
    if (typeof errs === 'object' && !Array.isArray(errs) && (errs.fieldErrors || errs.formErrors)) {
      const messages = [
        ...(errs.formErrors || []),
        ...Object.entries(errs.fieldErrors || {})
          .filter(([, msgs]) => msgs?.length)
          .map(([field, msgs]) => `${humanizeFieldName(field)}: ${msgs.join(', ')}`),
      ];
      if (messages.length) return messages.join('; ');
    }
    if (Array.isArray(errs)) return errs.map(e => e.msg).join(', ');
  }
  if (data?.error) return data.error;
  // No specific server message — map the status (or a network error) to copy a
  // user can act on, rather than axios's default "Request failed with status…".
  const status = error.response?.status;
  if (status && STATUS_MESSAGES[status]) return STATUS_MESSAGES[status];
  if (!error.response && (error.request || error.code)) {
    return "Can't reach the server. Check your connection and try again.";
  }
  if (error.message) return error.message;
  return fallback;
};

/**
 * Previously appended ?token= for <img>/<video> auth.
 * Now a no-op — cookies are sent automatically for same-origin requests.
 * Kept to avoid updating all call sites.
 */
export const withOwnerToken = (url) => url;

export default api;
